import { TagTextData } from '../tag-text/tag-text.component';
import {
  Module,
  TextAlign,
  TextPosition,
} from 'projects/common/model/ModuleModel';
import { ButtonOptions } from '../eva-button/eva-button.component';
import { TipMessage } from '../tip-message/tip-message.model';
import { TripProductType } from '../wizumen/wizumen-models';

export interface OfferModule extends Module<OfferCardData[]> {
  title: TagTextData;
  subtitle: TagTextData;
  pretitle: TagTextData;
  more_text: TagTextData;
  text_position: TextPosition;
  text_horizontal_align: TextAlign;
  offer_view_type: OfferViewType;
  button: ButtonOptions;
  detail_offer: boolean;
  tip_message: TipMessage;
  logo: {
    src: string;
  };
  grid_mode: boolean;
  slider_mode: boolean;
  ranked_items: boolean;
  offer_card_type: OfferCardType;
  offer_card_subtype?: OfferCardSubtype;
  view_mode?: ViewMode;
  force_currency_code?: string;
  sbox_autocomplete?: boolean;
  desktop_visible_rows?: number;
  mobile_visible_items?: number;
  referer_name?: string;
}

export enum OfferViewType {
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
  HIGHLIGHTED = 'HIGHLIGHTED',
}

export enum ViewMode {
  BANNER = 'BANNER',
  DEFAULT = 'DEFAULT'
}

export interface OfferCardData {
  id: string;
  offer_type: string;
  main_text: string;
  auxiliary_text: string;
  small_text: string;
  outstanding_text: string;
  outstanding_driver?: OfferCardDataDriver;
  room_type_description?: string;
  picture_link: string;
  pictures_carrousel: string[];
  link: Link;
  price: {
    [key: string]: OfferCardDataPrice;
  };
  price_child: {
    [key: string]: OfferCardDataPrice;
  };
  offer_alternatives: OfferCardDataAlternative[];
  stars: string;
  overall_rating: string;
  overall_rating_description?: string;
  overall_rating_description_style?: string;
  total_comments_text: string;
  payments: string;
  payments_media_key: string;
  flexible: boolean;
  drivers: OfferCardDataDriver[];
  information_icons: OfferCardDataInformationIcon[];
  dates: OfferCardDataDates;
  // TODO: Tracking data?
  position?: number;
  product: string;
  product_text: string;
  description: string;
  detail_items: { [key: string]: DetailItem };
  see_more_offers_link: Link;
  two_pax_promo: boolean;
  loyalty_info: LoyaltyInfo;
  promoted: boolean;
  reference_date: string;
  mealplans_driver?: OfferCardDataDriver,
  highlights: string[],
  additional_info: string,
  recommendation_id: string;
  recommendation_position: number,
  force_currency_code?: string;
  force_domain?: string;
  destination_city?: string;
  offer_tracking_data?: OfferTrackingData
  ribbon_top_card?: RibbonCardData;
  information_list?: InformationList[];
  item_type?: string;
  modal?: ModalCommon;
  offer_card_type: OfferCardType;
  offer_card_subtype?: OfferCardSubtype;
  flags?: string[];
  shopping_cart_link?: Link;
}

interface InformationList {
  icon: string;
  text: string;
}

interface RibbonCardData {
  type: string;
  text: string;
}

interface LoyaltyInfo {
  accrual_points: number;
}

export interface Link {
  url: string;
  title: string;
  type?: TripProductType;
  auxiliary_text?: string;
  icon_key?: string;
  scroll_target?: string;
}

export interface DetailItem {
  type: string;
  airline_code: string;
  text: string;
  from: string;
  to: string;
  baggage_info: BagggageInfo;
  overall_rating: string;
  regime: string;
  stars: string;
  auxiliary_text?: string
}

export interface BagggageInfo {
  backpack: boolean;
  carry_on: boolean;
  checked_bag_quantity: number;
}

export interface OfferCardDataDriver {
  text: string;
  type: string;
  icon?: string;
  icon_src?: string;
  tooltip?: {
    title: string; // from favs v2
    text: string;
  };
  driver_style: string;
  text_style: string;
  eva_class?: string; // from favs v2
}

export interface OfferCardDataInformationIcon {
  icon: string;
  tooltip_text?: string;
}

export enum PromoType {
  NONE = 'NONE',
  COMMON = 'COMMON',
  CROSS_SELLING = 'CROSS_SELLING',
  COUPON = 'COUPON',
  HOTSALE = 'HOTSALE',
  LOGGED_IN = 'LOGGED_IN',
  LOYALTY = 'LOYALTY',
  IFOOD = 'IFOOD'
}

export interface PromotionItem {
  promo_type: PromoType;
  ribbon: string;
  is_crossed_out: boolean;
}

export interface OfferCardDataPrice {
  currency_mask: string;
  total_amount: string;
  total_amount_not_formatted: number;
  message: string;
  tax_message: string;
  disclaimer?: string;
  info_message?: string;
  points?: number;
  prepaid_price_in_points?: number;
  promo: {
    real_price: string;
    real_price_not_formatted: number;
    promo_type: PromoType;
    ribbon?: string;
    discount?: number;
    discount_top_label?: {
      label: string;
      discount_text: string;
    };
  };
  promotions_list?: PromotionItem[];
  price_per_days_message?: string;
  per_day?: string;
}


export interface OfferCardDataAlternative {
  date_label_from: string;
  date_label_to: string;
  outstanding_text: string;
  price: { [key: string]: OfferCardDataPrice };
  link: {
    url: string;
    title: string;
  };
}

export interface OfferCardDataDates {
  from?: {
    date: string;
    text: string;
  };
  to?: {
    date: string;
    text: string;
  };
}

export interface OfferTrackingData {
  origin_gid?: string;
  destination_gid?: string;
  country_code?: string;
  assistance_code?: string;
  hotel_id?: string;
  cruise_id?: string;
  stop_gid?: string;
  tracking_id?: string;
}

export enum OfferCardType {
  COMMON = 'COMMON',
  BASIC = 'BASIC',
  DETAILED = 'DETAILED',
  REDUCED = 'REDUCED',
  SIMPLIFIED = 'SIMPLIFIED',
  MINI = 'MINI',
  WOW = 'WOW',
  SUMMARY = 'SUMMARY',
  // Deprecado, reemplazar por Summary 
  FLIGHT_LANDING = 'FLIGHT_LANDING',
  LIST = 'LIST',
}

export enum OfferCardSubtype {
  MULTIDESTINATION_SINGLE_PRODUCT = 'MULTIDESTINATION_SINGLE_PRODUCT',
  MULTIDESTINATION_MULTIPRODUCT = 'MULTIDESTINATION_MULTIPRODUCT',
  SINGLE_DESTINATION_SINGLE_PRODUCT = 'SINGLE_DESTINATION_SINGLE_PRODUCT',
  AIRLINE = 'AIRLINE',
  SIMPLIFIED_HALF = 'SIMPLIFIED_HALF',
  SIMPLIFIED_ROW = 'SIMPLIFIED_ROW',
  SIMPLIFIED_SQUARE = 'SIMPLIFIED_SQUARE',
  SIMPLIFIED_DESTINATION_EXPLORER = 'SIMPLIFIED_DESTINATION_EXPLORER',
}

// MODAL INFERFACE
export interface ModalCommon {
  component: string;
  props: ModalProps;
}

export interface ModalProps {
  header: ModalSection;
  content: ModalSection;
  bottom: ModalSection;
}

export interface ModalSection {
  title: string;
  components: ModalComponent[];
}

export interface ModalComponent {
  component: string;
  props: ModalData;
}

export interface ModalData {
  title?: string;
  text?: string;
  subtitle?: string;
  variant?: string;
  classes?: string;
  items?: OfferCardDataDriver[];
  driver?: OfferCardDataDriver;
}
